<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 10/12/2021
  -->

<template>

	<div class="cy-editor-root relative" :class="rootClassComputed" v-bind="attrsNoListeners">
		<div ref="cyDiv" class="cy-editor-root__stage">
			<div v-if="showToolbarComputed" :id="containerSelectorToolbar" class="cy-toolbar"></div>
			<div id="editor" :style="{top: editorTopComputed}">
				<div v-if="showLeftPanelComputed" :id="containerSelectorLeft" class="left" :style="{'display': leftPanelDisplayComputed}"></div>
				<div :id="containerSelectorCyDiv" class="cy-main"></div>
				<CanvasGuidanceToast :visible="showOnboardingToast" :message="onboardingToastMessage" :placement="onboardingToastPlacement"/>
				<div v-if="isGroupingModeActive && !shouldHideEditorChrome" class="grouping-mode-helper card p-3">
					<div class="bold mb-2">{{ groupingModeTitle }}</div>
					<div class="text-muted mb-3">{{ groupingModeText }}</div>
					<FormTextRow
						v-model="groupingDraftTitle"
						class="mb-3"
						title="Group title"
						:is-vertical="true"
						:multiline="false"
						@update:model-value="onGroupingDraftTitleChanged"/>
					<div class="mb-3">
						<div class="text-muted mb-1">Group color</div>
						<PaletteColorPicker
							class="my-2"
							v-model="groupingDraftColor"
							:colors="groupColorPalette"
							:inline="true"
							:color-size="26"
							@change="onGroupingDraftColorChanged"/>
					</div>
					<div class="qw-row align-items-center">
						<div class="text-muted">{{ groupingSelectionCount }} selected</div>
						<div class="layout-spacer"/>
						<qw-button-danger v-if="isEditingGroups" class="mr-2" @click="deleteNodeGroup">
							Delete
						</qw-button-danger>
						<qw-button class="mr-2" @click="cancelNodeGroup">
							Cancel
						</qw-button>
						<qw-button type="primary" :disabled="groupingSelectionCount < 2" @click="saveNodeGroup">
							{{ groupingModeSaveLabel }}
						</qw-button>
					</div>
				</div>

				<div class="overflow-y" style="z-index: 1001" v-if="!shouldHideEditorChrome && hasSelected && !isGroupingModeActive">
<!--					<slot name="detailsPanel"-->
<!--						  :clientProfile="clientProfile"-->
<!--						  :dataManagerProp="dataManager"-->
<!--						  :selectedNodes="selectedNodes"-->
<!--						  :selectedEdges="selectedEdges"-->
<!--						  :cyEditor="cyComputed">-->
							<ElementDetailsPanel
								ref="elementDetailsPanel"
								:cyEditor="cyComputed"
								:client-profile="clientProfile"
								:dataManagerProp="dataManager"
								:hideFamilyRelationships="hideFamilyRelationships"
								:readonly="readonly"
								:customConnectionsSyncManager="customConnectionsSyncManager"
								:width="widthComputed"
							:genogramItem="genogramItem"
							:selectedNodes="selectedNodes"
							:selectedEdges="selectedEdges">
							<template #nodeDetailsPanel="data">
								<slot name="nodeDetailsPanel" v-bind="data"></slot>
							</template>
							<template #edgeDetailsPanel="data">
								<slot name="edgeDetailsPanel" v-bind="data"></slot>
							</template>
						</ElementDetailsPanel>
<!--					</slot>-->
				</div>
			</div>
		</div>
		<div v-if="!embeddedLegend" class="fixed"
			 :style="{'bottom': legendBottomComputed, 'right': '10px', 'z-index': 1000}">
				<GenogramLegendView
					ref="legendView"
					legendWidth="300px"
					:legendInitialVisibility="genogramItem.isLegendVisible"
					:genogram-item="genogramItem"
					:customConnectionsSyncManager="customConnectionsSyncManager"
					v-show="showLegend"
				v-if="cyComputed"
				:cyEditor="cyComputed"
				@legendVisibilityChange="onLegendVisibilityChanged"
				@patternExplorationModeChanged="onPatternExplorationModeChanged"/>
		</div>
		<EmbeddedGenogramLegendView
			v-if="embeddedLegend"
			ref="legendView"
			:genogram-item="genogramItem"
			:customConnectionsSyncManager="customConnectionsSyncManager"
			:cy="isReady ? cy : null"
		/>
	</div>

</template>

<script>
import './assets/index.css'

import ClientProfile from "@/core/js/model/Profile/ClientProfile";

import CyEditor from "./lib";
import { defineAsyncComponent } from 'vue'
//we lazy load the ElementDetailsPanel for initial load performance since it's only needed first when the user selects an element
let ElementDetailsPanel = defineAsyncComponent(() => import("@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/ElementDetailsPanel.vue"));
// import ElementDetailsPanel from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/ElementDetailsPanel.vue";
import Debouncer from "@shared/Debouncer";
import GenogramLegendView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/GenogramLegendView.vue";
import EmbeddedGenogramLegendView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/EmbeddedGenogramLegendView.vue";
import GenogramItem from "@shared/Genogram/GenogramItem";
import BaseComponent from "@/core/components/BaseComponent.vue";
import {uuidv4} from "@/utils/helpers.js";
import {createDialogLazy} from "@/utils/helpers-external";
import {isMobileScreen} from "@/core/screenHelper";
import debounce from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/utils/debounce";
import PaletteColorPicker from "@/core/components/PaletteColorPicker.vue";
import {healthConditionColorPickerPalette} from "@/core/js/PaletteEnum";
import FormTextRow from "@/core/components/Form/FormTextRow.vue";
import QwButtonDanger from "@/core/components/Controls/Buttons/QwButtonDanger.vue";
import GenogramCustomConnectionsSyncManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramCustomConnectionsSyncManager";
import GenogramGuidanceManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramGuidanceManager";
import CanvasGuidanceToast from "@/core/components/CanvasGuidance/CanvasGuidanceToast.vue";
import {isGenogramNodeSizeScaleEligible} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramNodeSizeScaleHelper";

let GenogramLinePickerDialog = () => import("@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/GenogramLinePickerDialog.vue")
let genogramLinePickerDialog = createDialogLazy(GenogramLinePickerDialog)

export default {
	components: {QwButtonDanger, FormTextRow, PaletteColorPicker, GenogramLegendView, EmbeddedGenogramLegendView, ElementDetailsPanel, CanvasGuidanceToast},
	extends: BaseComponent,

	name: 'CyEditorView',
	props: {
		clientProfile: {ClientProfile, required: true},
		genogramItem: {GenogramItem, required: true},
		cyConfig: {Object, default: () => ({})},
		editorConfig: {Object, default: () => ({})},
		embedded: Boolean,
		embeddedLegend: Boolean,
		readonly: Boolean,
		hideFamilyRelationships: Boolean,
		customConnectionsSyncManager: {type: GenogramCustomConnectionsSyncManager, default: null},
	},

	inheritAttrs: false,

	data: function() {
		this.cyEditor = null //non-reactive cyEditor
		this.cy = null, //non-reactive cy. Critical for subtle UI refresh bugs where nodes sometimes are not refreshing on type/shape or label change.
		this.embeddedWheelListener = null
		this.resizeObserver = null
		this.refreshViewportFrameId = null
		this.refreshViewportTimeoutId = null
		this.genogramGuidanceManager = null
		this.genogramEnvChangedListener = null
		return {
			actionListenerExternal: null,
			showLegend: true,
			isReady: false,
			emitChangeDebouncer: null,
			hasSelected: false,
			selectedNodes: [],
			selectedEdges: [],
			containerSelectorCyDiv: "cy-" + uuidv4(),
			containerSelectorLeft: "cy-left-" + uuidv4(),
			containerSelectorToolbar: "cy-toolbar-" + uuidv4(),
			leftPanelVisible: true,
			isGroupingModeActive: false,
			isEditingGroups: false,
			groupingDraftColor: healthConditionColorPickerPalette[0],
			groupingDraftTitle: "",
			showOnboardingToast: false,
			onboardingToastMessage: "",
			onboardingToastPlacement: "bottom-center",
		}
	},

	created() {
		this.onGenogramChangedDebounced = new Debouncer(this.onGenogramChanged.bind(this), 1000)
		if(isMobileScreen()){
			this.onLegendVisibilityChanged(false)
		}
		this.genogramGuidanceManager = new GenogramGuidanceManager({
			canShowOnboarding: () => this.canShowOnboardingToast(),
			canShowHelper: () => this.canShowGuidanceHelper(),
			notifyStateChanged: (state) => this.onOnboardingStateChanged(state),
		})
	},

	mounted() {
		const container = this.$refs.cyDiv
		this.setupEmbeddedWheelInterception(container)
		const defaultFn = () => {
			console.log("Genogram.default action listener")
		}
		this.actionListenerExternal = this.editorConfig.actionListener || defaultFn
		delete this.editorConfig.actionListener
			let config = {
			cy: {
				...this.cyConfig,
				autolock: false,
				readonly: this.readonly,
				container: "#" + this.containerSelectorCyDiv,
				containerSelectorLeft: "#" + this.containerSelectorLeft,
				containerSelectorToolbar: "#" + this.containerSelectorToolbar,
			},
			editor: {
				container,
				actionListener: this.actionListener.bind(this),
				customConnectionRenderManager: this.customConnectionsSyncManager?.getRenderManager?.() || null,
				customConnectionsSyncManager: this.customConnectionsSyncManager,
				...this.editorConfig
			}
		}
		this.cyEditor = new CyEditor(config, this.$analyticManager)
		this.cyEditor.setGenogramItem(this.genogramItem)
		this.cy = this.cyEditor.cy
		this.setupResizeObserver()
		this.cyEditor.on("groupingModeChanged", this.onGroupingModeChanged, this)
		this.cyEditor.on("onboardingDragAddCompleted", this.onOnboardingDragAddCompleted, this)
		this.cyEditor.on("onboardingTapAddRequested", this.onOnboardingTapAddRequested, this)
		this.cyEditor.on("onboardingTapAddPlacementAttempted", this.onOnboardingTapAddPlacementAttempted, this)
		this.cyEditor.on("onboardingTapAddCancelled", this.onOnboardingTapAddCancelled, this)
		this.cyEditor.on("nodeAddOverlapDetected", this.onNodeAddOverlapDetected, this)
		this.cyEditor.on("nodeDragLearnedForOverlapHint", this.onNodeDragLearnedForOverlapHint, this)

		this.showElementsInfoDebounced = debounce(this.showElementsInfo, 50)

		let defaultJsonParams = {
			userPanningEnabled: true,
			userZoomingEnabled: true,
			panningEnabled: true,
			zoomingEnabled: true
		}
		this.cyEditor.json({
			...this.genogramItem.jsonData,
			...defaultJsonParams
		})

		this.cyEditor.on('change', () => {
			this.onGenogramChangedDebounced.onChangeDebounced()
		})

		this.cyEditor.on('requestLegendSnapshot', async () => {
			return await this.makeLegendSnapshot()
		})
		this.cyEditor.on('requestClearLegendInteraction', async () => {
			return await this.clearLegendInteraction()
		})
		this.setupGenogramEnvListener()
		this.isReady = true
		this.onCyReady()
		this.genogramGuidanceManager?.start()
	},

	beforeUnmount() {
		this.genogramGuidanceManager?.destroy()
		this.cyEditor?.off("groupingModeChanged", this.onGroupingModeChanged, this)
		this.cyEditor?.off("onboardingDragAddCompleted", this.onOnboardingDragAddCompleted, this)
		this.cyEditor?.off("onboardingTapAddRequested", this.onOnboardingTapAddRequested, this)
		this.cyEditor?.off("onboardingTapAddPlacementAttempted", this.onOnboardingTapAddPlacementAttempted, this)
		this.cyEditor?.off("onboardingTapAddCancelled", this.onOnboardingTapAddCancelled, this)
		this.cyEditor?.off("nodeAddOverlapDetected", this.onNodeAddOverlapDetected, this)
		this.cyEditor?.off("nodeDragLearnedForOverlapHint", this.onNodeDragLearnedForOverlapHint, this)
		this.destroyGenogramEnvListener()
		this.destroyResizeObserver()
		this.removeEmbeddedWheelInterception()
	},

	methods: {
		setupEmbeddedWheelInterception(container) {
			if(!this.embedded || !container){
				return
			}

			this.embeddedWheelListener = (event) => {
				if(event.ctrlKey || event.metaKey){
					return
				}

				event.stopImmediatePropagation()
			}

			container.addEventListener('wheel', this.embeddedWheelListener, {capture: true})
		},

		removeEmbeddedWheelInterception() {
			let container = this.$refs.cyDiv
			if(!container || !this.embeddedWheelListener){
				return
			}

			container.removeEventListener('wheel', this.embeddedWheelListener, {capture: true})
			this.embeddedWheelListener = null
		},

		setupResizeObserver() {
			let resizeTarget = document.getElementById(this.containerSelectorCyDiv)
			if(!resizeTarget || typeof ResizeObserver !== "function"){
				return
			}

			this.resizeObserver = new ResizeObserver(() => {
				this.refreshViewportDebounced()
			})
			this.resizeObserver.observe(resizeTarget)
		},

		destroyResizeObserver() {
			this.resizeObserver?.disconnect()
			this.resizeObserver = null
			if(this.refreshViewportTimeoutId){
				clearTimeout(this.refreshViewportTimeoutId)
				this.refreshViewportTimeoutId = null
			}
			if(this.refreshViewportFrameId && typeof cancelAnimationFrame === "function"){
				cancelAnimationFrame(this.refreshViewportFrameId)
				this.refreshViewportFrameId = null
			}
		},
		setupGenogramEnvListener() {
			if(typeof window === "undefined"){
				return
			}

			this.genogramEnvChangedListener = () => {
				this.genogramGuidanceManager?.syncState()
			}
			window.addEventListener("qw-genogram-env-changed", this.genogramEnvChangedListener)
			window.addEventListener("resize", this.genogramEnvChangedListener)
			window.addEventListener("orientationchange", this.genogramEnvChangedListener)
		},
		destroyGenogramEnvListener() {
			if(typeof window === "undefined" || !this.genogramEnvChangedListener){
				return
			}

			window.removeEventListener("qw-genogram-env-changed", this.genogramEnvChangedListener)
			window.removeEventListener("resize", this.genogramEnvChangedListener)
			window.removeEventListener("orientationchange", this.genogramEnvChangedListener)
			this.genogramEnvChangedListener = null
		},

		refreshViewportDebounced() {
			if(this.refreshViewportTimeoutId){
				clearTimeout(this.refreshViewportTimeoutId)
			}
			this.refreshViewportTimeoutId = setTimeout(() => {
				this.refreshViewportTimeoutId = null
				if(this.refreshViewportFrameId){
					return
				}
				this.refreshViewportFrameId = window.requestAnimationFrame(async () => {
					this.refreshViewportFrameId = null
					await this.$nextTick()
					this.cyEditor?.refreshViewport()
				})
			}, 80)
		},

		async actionListener(command, params) {
			let res = await this.actionListenerExternal(command, params)
			console.log("CyEditorView.actionListener: ", command, res)
			if(res !== undefined){
				return res
			}

			switch (command.action) {
				case "toggleLeftPanel":
					this.leftPanelVisible = !this.leftPanelVisible
					this.refreshViewportDebounced()
					return true
				case "showManualConnectionLinePicker":
					return await this.showManualConnectionLinePicker(params)
				case "showToastInfo":
					await this.actionListenerExternal({action: "loadFullApp"}, {progress: true})
					this.toastInfo(params.message, params.duration)
					return true
				case "showToastWarning":
					await this.actionListenerExternal({action: "loadFullApp"}, {progress: true})
					this.toastWarn(params.message, params.duration)
					return true
			}

			return undefined //indicates that the action was not handled
		},

		async showManualConnectionLinePicker(params = {}) {
			return await genogramLinePickerDialog({
				hideFamilyRelationships: this.hideFamilyRelationships,
				customConnectionsSyncManager: this.customConnectionsSyncManager,
				onCustomConnectionsChanged: this.onLinePickerCustomConnectionsChanged,
				initialTabName: params.initialTabName,
			})
		},

		async onLinePickerCustomConnectionsChanged() {
			await this.customConnectionsSyncManager?.loadDefinitions?.()
			this.customConnectionsSyncManager?.syncCyEdges?.(this.cy)
		},

		onCyReady() {
			console.log("onCyReady: ", this.cy)
			this.cy.on('select unselect remove', (e) => {
				console.log("showElementsInfo: e = ", e)
				this.showElementsInfoDebounced() //we need to debounce this to ensure that the selection is correct because the selection events are fired multiple times
			})
			this.cy.on('tap', (e) => {
				console.log("showElementsInfo2: e = ", e)
				this.genogramGuidanceManager?.clearTapToAddMessage()
				if(this.$refs.elementDetailsPanel){
					this.$refs.elementDetailsPanel.resetVisibility({force: true})
				}
			})
			if(this.editorConfig.fitOnOpen){
				this.cy.fit(undefined, 20)
			}
			this.syncLabels()
		},

		syncLabels(){
			let nodes = this.cy.nodes()
			nodes.forEach(node => {
				let data = node.data()
				this.genogramItem.updateNodeLabel(data)
				node.data(data)
			})
			this.cyEditor.syncEdgeLabels()
			this.cyEditor.syncNodeGroups()
		},

		async showElementsInfo() {
			console.log("showElementsInfo: start")
			let selected = this.cy.$(':selected')
			let selectedNodes = selected.map(elem => elem.isNode() ? this.createSelectedElementDetailsData(elem.data()) : null).filter(e => e != null)
			let selectedEdges = selected.map(elem => elem.isEdge() ? this.createSelectedElementDetailsData(elem.data()) : null).filter(e => e != null)
			let hasSingleSelectionDetails = selected.length === 1 && !selected[0].data().isAuxNode
			let hasBulkNodeSelectionDetails = selectedNodes.length > 1 && selectedNodes.some(node => isGenogramNodeSizeScaleEligible(node))
			let hasSelected = hasSingleSelectionDetails || hasBulkNodeSelectionDetails

			if(this.hasSelected !== hasSelected){
				await this.emitPromise("detailsVisibilityChanged", hasSelected)
			}
			this.hasSelected = hasSelected
			this.showLegend = !this.hasSelected && !this.isGroupingModeActive
			console.log("showElementsInfo: selected = ", selected)
			this.selectedNodes = selectedNodes
			this.selectedEdges = selectedEdges
			this.refreshViewportDebounced()
			if(this.$refs.elementDetailsPanel){
				this.$refs.elementDetailsPanel.resetVisibility()
			}
			console.log("showElementsInfo: end")
		},

		createSelectedElementDetailsData(elementData) {
			if(!elementData){
				return null
			}
			return JSON.parse(JSON.stringify(elementData))
		},

		async makeLegendSnapshot(){
			this.showLegend = true
			await this.$nextTick()
			let img = await this.$refs.legendView.requestSnapshot()
			return img
		},
		async clearLegendInteraction() {
			this.$refs.legendView?.clearLegendHoverState?.()
			await this.$nextTick()
			return true
		},

		onElementDetailsPanelVisibilityChange(isPanelVisible) {
			this.showLegend = !isPanelVisible
		},

		onLegendVisibilityChanged(isVisible) {
			if(this.readonly){
				return
			}
			this.genogramItem.isLegendVisible = isVisible
			this.$emit("change", this.genogramItem)
		},
		onPatternExplorationModeChanged(isActive) {
			this.genogramGuidanceManager?.setPatternExplorationModeActive(isActive)
		},
		onOnboardingStateChanged(state = {}) {
			this.showOnboardingToast = !!state.isVisible
			this.onboardingToastMessage = state.message || ""
			if(state.isVisible){
				this.onboardingToastPlacement = state.placement || "bottom-center"
			}
		},
		onOnboardingDragAddCompleted() {
			this.genogramGuidanceManager?.handleDragAddCompleted()
		},
		onOnboardingTapAddRequested(event, params = {}) {
			this.genogramGuidanceManager?.showTapToAddMessage(params.itemName, params.itemType)
		},
		onOnboardingTapAddPlacementAttempted() {
			this.genogramGuidanceManager?.clearTapToAddMessage()
		},
		onOnboardingTapAddCancelled() {
			this.genogramGuidanceManager?.clearTapToAddMessage()
		},
		onNodeAddOverlapDetected() {
			this.genogramGuidanceManager?.handleNodeAddOverlapDetected()
		},
		onNodeDragLearnedForOverlapHint() {
			this.genogramGuidanceManager?.handleNodeDragLearned()
		},

		getCyEditor() {
			return this.cyEditor
		},
		canShowOnboardingToast() {
			let canShow = false
			if(!this.readonly && !this.shouldHideEditorChrome){
				canShow = true
			}
			return canShow
		},
		canShowGuidanceHelper() {
			let canShow = false
			if(!this.readonly && !this.shouldHideEditorChrome){
				canShow = true
			}
			return canShow
		},

		async onGroupingModeChanged(event, state = {}) {
			let shouldShowGroupingHelper = !!state.isGroupingModeActive
			if(shouldShowGroupingHelper){
				await this.actionListener({action: "loadFullApp"}, {progress: true})
			}
			this.isGroupingModeActive = !!state.isGroupingModeActive
			this.isEditingGroups = !!state.isEditingGroups
			this.groupingDraftColor = state.draftGroupColor || healthConditionColorPickerPalette[0]
			this.groupingDraftTitle = state.draftGroupTitle || ""
			this.showLegend = !this.hasSelected && !this.isGroupingModeActive
		},
		onGroupingDraftTitleChanged(title) {
			this.cyEditor?.setDraftNodeGroupTitle(title)
		},
		onGroupingDraftColorChanged(color) {
			this.cyEditor?.setDraftNodeGroupColor(color)
		},

		async saveNodeGroup() {
			await this.cyEditor?.saveNodeGroupDraft()
		},

		cancelNodeGroup() {
			this.cyEditor?.cancelNodeGroupDraft()
		},

		async deleteNodeGroup() {
			let shouldDelete = await this.cyEditor?.notifyAction("showMessageYesNo", {
				title: "Delete group",
				message: "Are you sure you want to delete this group outline?<br/><br/>The people will not be deleted from the genogram.",
				okTitle: "Delete",
			})
			if(shouldDelete === false){
				return
			}
			this.cyEditor?.deleteEditingNodeGroups()
		},

		onGenogramChanged() {
			if(this.readonly){
				return
			}
			let json = this.cyEditor.json()
			if (json.elements.nodes) {
				json.elements.nodes = json.elements.nodes.filter(node => !node.data.tempElement)
			}
			console.log("genogram changed: ", json)
			this.genogramItem.jsonData = json
			this.$emit("change", this.genogramItem)
		},
	},
	computed: {
		rootClassComputed() {
			return {
				"cy-editor-root--embedded": this.embedded,
			}
		},
		shouldHideEditorChrome() {
			return this.embedded
		},

		cyComputed() {
			return this.isReady && this.cyEditor ? this.cyEditor : null
		},
		widthComputed(){
			let width
			if(this.editorConfig.rightPanel){
				width = this.editorConfig.rightPanel.width
			}
			return width || "300px"
		},
		showToolbarComputed() {
			return !this.shouldHideEditorChrome
		},
		showLeftPanelComputed() {
			return !this.shouldHideEditorChrome && !this.readonly
		},
		editorTopComputed() {
			return this.showToolbarComputed ? '44px' : '0'
		},
		groupingModeTitle() {
			if(this.isEditingGroups){
				return "Edit group"
			}
			return "Select people to group"
		},
		groupingModeText() {
			if(this.isEditingGroups){
				return "Click people to add or remove from the group."
			}
			return "Click people to add or remove from the group."
		},
		groupingModeSaveLabel() {
			if(this.isEditingGroups){
				return "Save"
			}
			return "Save"
		},
		groupColorPalette() {
			return healthConditionColorPickerPalette
		},

		leftPanelDisplayComputed(){
			if(!this.showLeftPanelComputed){
				return "none"
			}
			return this.leftPanelVisible ? "block" : "none"
		},
		legendBottomComputed(){
			return this.cyConfig.legendBottom || "10px"
		},
		groupingSelectionCount() {
			return this.selectedNodes.filter(node => !node.isAuxNode).length
		},
	},


}
</script>

<style lang="scss">

.cy-editor-root{
	&.cy-editor-root--embedded{
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	.cy-editor-root__stage{
		position: relative;
		width: 100%;
		height: 100%;
		min-height: inherit;
	}

	&.cy-editor-root--embedded .cy-editor-root__stage{
		flex: 1 1 auto;
		min-height: 0;
	}

	&.cy-editor-root--embedded #editor{
		border: 0;
	}

	.grouping-mode-helper{
		position: absolute;
		top: 12px;
		right: 12px;
		width: min(420px, calc(100% - 24px));
		z-index: 1002;
	}

	.symbols-tooltip{
		background: darkgray;
	}
	.symbols-tooltip::after {
		opacity: 0;
	}
	.symbols-tooltip {
		&:focus, &:hover, &:active {
			&::after {
				content: attr(data-tooltip);
				opacity: 1;

				animation-name: show;
				animation-duration: 0.5s;
				font-size: 12px;
				width: auto;
				height: auto;
				max-width: 200px;
				white-space: nowrap;
				background-color: #555;
				color: #fff;
				text-align: center;
				border-radius: 3px;
				padding: 4px 8px;
				position: absolute;
				z-index: 1000;
				//bottom: 125%;
				//left: 50%;

				top: 30px;
			}
		}

	}

	//.symbols-tooltip:focus::after, .symbols-tooltip:hover::after {
	//
	//}

	.cy-toolbar{
		[class^="icon-"]:before, [class*=" icon-"]:before {
			//font-family: inherit !important;
			margin-left: 0 !important;
			//margin-right: 0 !important;
		}
		.icon-to-png{
			font-family: "fontello" !important;
		}
		.icon-to-png:before {
			content: "\e82a";
		}

		.command{
			position: relative;
		}
		.command::after{
			opacity: 0;
		}


		.command:hover:not(.touch-device)::after{
			animation-name: show;
			animation-duration: 0.5s;
			opacity: 1;
			font-size: 12px;
			content: attr(data-title);
			width: auto;
			white-space: nowrap;
			background-color: #555;
			color: #fff;
			text-align: center;
			border-radius: 3px;
			padding: 4px 8px;
			position: absolute;
			z-index: 1000;
			//bottom: 125%;
			left: 50%;

			top: 30px;
		}

		@keyframes show {
			0% {
				opacity: 0;
			}

			99% {
				opacity: 0;
			}

			100% {
				opacity: 1;
			}
		}
	}
}


</style>
